import { useEffect, useMemo, useState } from "react";
import { debounce } from "../../utils/helper";
import { Flex, Input, Switch, Table, Tag, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { ISortInfo } from "../../interfaces/common";
import { InvoiceServices } from "../../services/invoice";
import { IInvoiceResponse, IInvoiceResult } from "../../interfaces/invoice";
import { getColumns, getExpandedColumns, getExpandedRecordColumns } from "./components/columns";
import { ISupplierInvoiceRecordResult } from "../../interfaces/supplier-invoice-record";
import "./invoice.scss";
import { useViewFile } from "../../hooks/useViewFile";
import { SupplierInvoiceRecordStatusEnum } from "../../constants/enum";
import { usePageStore } from "../../context/PageStoreContext";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PAGINATION } from "../../constants/pagination";

const InvoicePageDebug: React.FC = () => {
  const navigate = useNavigate();
  const [listInvoice, setListInvoice] = useState<IInvoiceResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string|undefined>(undefined);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string|undefined>(undefined);
  const [aiLimitationOnly, setAiLimitationOnly] = useState<boolean|undefined>(undefined);
  const { loading: loadingFile, onViewFile: onDownloadAttachment } = useViewFile();
  const { setPageValue, getPageValue } = usePageStore();
  
  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    setAiLimitationOnly(!!getPageValue().aiLimitation);
    setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const searchValueFilter = debouncedSearchValue ?? getPageValue().searchValue;
      const value = aiLimitationOnly ?? !!getPageValue().aiLimitation;
      const params = {
        keyword: searchValueFilter,
        aiLimitation: value === false ? undefined : value,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: IInvoiceResponse = await InvoiceServices.getAllDebug(params);
      setListInvoice(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch invoices error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (debouncedSearchValue !== undefined) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [debouncedSearchValue, pagination.current, pagination.pageSize, sortInfo, aiLimitationOnly]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const setPageValueInvoice = () => {
    setPageValue({
      searchValue,
      pagination,
      aiLimitation: aiLimitationOnly,
    });
  };

  const linkToAudit = (link: string) => {
    setPageValueInvoice();
    navigate(`/invoice-debug/${link}/audit-trail`);
  };

  const columns = getColumns(onDownloadAttachment, linkToAudit, pagination.current, pagination.pageSize);
  const expandedRowRender = (record: IInvoiceResult) => {

    let sumAmount = 0;
    (record.sirs || []).filter(s => s.status !== SupplierInvoiceRecordStatusEnum.REJECTED).forEach(({ amount }) => {
      sumAmount += amount ?? 0;
    });
    const diff = sumAmount - (record.totalAmount ?? 0);
    const EPSILON = 0.01;
    const isMatch = sumAmount > 0 && Math.abs(diff) < EPSILON;

    return (
      <>
        <div style={{ width: 900, marginLeft: "auto" }}>
          <Typography.Title level={5} style={{ marginBottom: 10, marginTop: -5 }}>Invoice</Typography.Title> Invoice Number: {record.invoiceNumber} | {record.attachmentName}
          <Table<IInvoiceResult>
            columns={getExpandedColumns()}
            dataSource={[record]}
            rowKey={(subRecord) => subRecord.id}
            pagination={false}
            size="small"
            showHeader={true}
          />
        </div>
        <div style={{ width: 900, marginLeft: "auto" }}>
          <Typography.Title level={5} style={{ marginBottom: 20 }}>
            Supplier Invoice Record{" "}
            {isMatch ? (
              <Tag color="green-inverse">MATCH</Tag>
            ) : (
              <Tag color="red">{`FALL`}: {`Mismatch: ${diff.toLocaleString()}`}</Tag>
            )}
          </Typography.Title>
          <Table<ISupplierInvoiceRecordResult>
            columns={getExpandedRecordColumns()}
            dataSource={record.sirs || []}
            rowKey={(subRecord) => subRecord.id}
            pagination={false}
            size="small"
            showHeader={true}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <strong style={{ color: "var(--text-blue-600)" }}></strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <div style={{ color: isMatch ? "blue" : "red", fontWeight: "bold" }}>
                    {sumAmount.toLocaleString()}
                    <br />
                    {!isMatch && ` (Mismatch: ${diff.toLocaleString()})`}
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>
      </>
    );
  };

  return (
    <div className="invoices-received-page">
      <h2 className="title-page">Invoice Debug</h2>
      <Flex gap={8} justify="start" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter search content here"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 300 }}
          size="middle"
          allowClear
        />

        <Switch
          checked={aiLimitationOnly}
          onChange={(checked) => {
            setLoading(true);
            setAiLimitationOnly(checked);
            setPagination((prev) => ({ ...prev, current: 1 })); // reset page
          }}
          checkedChildren="AI Limit"
          unCheckedChildren="All"
          style={{ height: 24, marginLeft: 8, width: 120 }} 
        />
      </Flex>

      <Table
        dataSource={listInvoice}
        loading={loading || loadingFile}
        rowKey={(record) => record.id}
        columns={columns}
        expandable={{
          expandedRowRender,
          defaultExpandedRowKeys: listInvoice
            .filter((item) => item.sirs?.length)
            .map((item) => item.id),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          let className = index % 2 === 0 ? "highlight-row" : "";
          if (record.deletedAt) {
            className = " disabled-row";
          }
          return className.trim();
        }}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default InvoicePageDebug;
